import React from 'react';

import type { AuthProps } from '../../auth';
import { useAuthDecision } from '../../auth';
import { useUIColors } from '../../theme';
import { cx, defineRecipe, useRecipe, type Extensible, type Styleable } from '../../variants';

/**
 * `filter` — the default: a bucket of a list, pressed when that bucket is the
 * one on screen. `toggle` — an independent on/off switch that sits in the same
 * row (frame 55a's "Ẩn app chưa sẵn sàng"); same box, but it is not one of a
 * mutually-exclusive set, so it never implies the others are off.
 */
export type FilterPillTone = Extensible<'filter' | 'toggle'>;

export type FilterPillSize = Extensible<'sm' | 'md'>;

export const filterPillRecipe = defineRecipe('filterPill', 'ui-filter-pill', {
    variants: {
        tone: { filter: 'ui-filter-pill--filter', toggle: 'ui-filter-pill--toggle' },
        size: { sm: 'ui-filter-pill--sm', md: 'ui-filter-pill--md' },
        // `yes`/`no`, not CVA's boolean keys — see `chipRecipe`'s note.
        selected: { yes: 'ui-filter-pill--selected', no: '' },
        denied: { yes: 'ui-filter-pill--denied', no: '' },
    },
    defaultVariants: { tone: 'filter', size: 'md', selected: 'no', denied: 'no' },
});

export interface FilterPillProps extends AuthProps, Styleable {
    children: React.ReactNode;
    /** Pressed state. Always pass it — a filter pill with no state is a button. */
    selected: boolean;
    onClick: () => void;
    tone?: FilterPillTone;
    /** `md` = 30px (a table toolbar), `sm` = 28px (a narrow rail). */
    size?: FilterPillSize;
    /**
     * Trailing match count. Rendered as tabular-nums so a row of pills does not
     * jitter as the numbers change, and omitted entirely when `undefined` —
     * a pill showing `0` reads as "zero matches" where the design's own
     * convention is to print no number at all for a bucket with no count.
     */
    count?: number;
    /** Accessible name, when `children` alone is not descriptive enough. */
    ariaLabel?: string;
}

/**
 * EAA-1817: the rounded filter pill every console toolbar and rail draws — a
 * bucket label, optionally a match count, pressed when its bucket is on
 * screen.
 *
 * WHY THIS IS NOT `Chip`. `Chip` says in its own docstring that `selected`
 * exists "for filter-pill usage", and that is true for the thing `Chip` is: a
 * MONOSPACE, radius-6, inset-ring chip that holds a machine value — a role key,
 * an app role. The pills in the handoff are a different object: radius-999,
 * proportional text at 12.5px, prose labels ("Cần xử lý", "Chưa phủ"), and a
 * solid brand fill when pressed. Rendering these through `Chip` would mean
 * either a `shape`/`font` axis that makes `Chip` two components wearing one
 * name, or callers overriding half its inline palette by `style` — which is
 * exactly the drift the kit exists to stop. They live side by side in one
 * toolbar in frame 55a (pills on the left, role-code chips in the grid), so the
 * two really are distinct and really do co-occur.
 *
 * WHY IT IS NOT `Segmented` EITHER. `Segmented` is one control with N
 * segments in a shared track; these are N independent pills that wrap onto a
 * second line when the toolbar is narrow, and one of them ("Ẩn app chưa sẵn
 * sàng") is not part of the exclusive set at all. A segmented control cannot
 * hold a member that is not one of the choices.
 *
 * SOURCE OF THE VALUES: `Portal 6 Ung dung va vai tro.dc.html` frames 55 (rail,
 * 28px) and 55a (table toolbar, 30px). The component sheet (Portal 8) has no
 * card for this control, so those two frames are the reference; both were read
 * as raw HTML, not from a screenshot.
 *
 * The colours come from `useUIColors()` and the classes from the recipe, so an
 * app can reskin it through `UIVariantsProvider` without this file changing.
 */
export function FilterPill({
    children,
    selected,
    onClick,
    tone = 'filter',
    size = 'md',
    count,
    ariaLabel,
    auth,
    authFallback = 'disable',
    className,
    style,
}: FilterPillProps): React.ReactElement {
    const colors = useUIColors();
    const decision = useAuthDecision(auth, authFallback);

    // Hooks before the `hide` guard — this component returns early below.
    const denied = decision !== 'allow';
    const recipeClassName = useRecipe(filterPillRecipe, {
        tone,
        size,
        selected: selected ? 'yes' : 'no',
        denied: denied ? 'yes' : 'no',
    });

    if (decision === 'hide') return <></>;

    const height = size === 'sm' ? 28 : 30;
    const paddingX = size === 'sm' ? 11 : 12;

    return (
        <button
            type="button"
            className={cx(recipeClassName, className)}
            aria-pressed={selected}
            aria-label={ariaLabel}
            disabled={denied}
            onClick={denied ? undefined : onClick}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                flex: 'none',
                height,
                padding: `0 ${paddingX}px`,
                border: 'none',
                borderRadius: 999,
                background: selected ? colors.primary : colors.surface,
                // Unpressed pills use an inset ring rather than a border so a
                // pressed and an unpressed pill occupy exactly the same box —
                // a border would make the row twitch by 2px on every click.
                boxShadow: selected ? undefined : `inset 0 0 0 1px ${colors.border}`,
                color: selected ? colors.textOnCta : colors.textSecondary,
                font: 'inherit',
                fontSize: 12.5,
                fontWeight: 500,
                lineHeight: 1,
                whiteSpace: 'nowrap',
                cursor: denied ? 'not-allowed' : 'pointer',
                opacity: denied ? 0.6 : 1,
                ...style,
            }}
        >
            {children}
            {count !== undefined && (
                <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{count}</span>
            )}
        </button>
    );
}

export default FilterPill;
